import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Login as LoginD } from '../contexts/Reducers/SessionSlice';
import { LysepAPI, setAPIToken } from '../contexts/ApiContext';
import LFEStore from '../contexts/SessionProvider';
import { useCookies } from 'react-cookie';
import { initializeFCM } from '../services/fcmService';
import { User, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';

export default function Login() {
    const isAuthenticated = useSelector(state => state.session.Authenticated);
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [state, setState] = useState({
        username: "",
        password: "",
        error: {
            state: false,
            text: ""
        },
        redirectToPanel: false
    });
    const [cookies, setCookie] = useCookies();

    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
        }

        const { username, password } = state;

        if (!username || !password) {
            setState(prev => ({
                ...prev,
                error: {
                    state: true,
                    text: "Kullanıcı adı ve şifre gerekli"
                }
            }));
            return;
        }

        setIsLoading(true);

        try {
            const res = await LysepAPI.post("/api/auth/login", { username, password });

            if (res.data.state) {
                LFEStore.dispatch(LoginD(res.data.user));
                setCookie("__lfe_state", true);
                setCookie("__lfe_token", res.data.user.token);
                setAPIToken(res.data.user.token);

                // Register FCM token after successful login
                try {
                    const fcmResult = await initializeFCM();
                    if (fcmResult.success) {
                        console.log('FCM token registered successfully');
                    } else {
                        console.warn('FCM token registration failed:', fcmResult.message);
                    }
                } catch (fcmError) {
                    console.error('Error registering FCM token:', fcmError);
                }

                setState(prev => ({ ...prev, redirectToPanel: true }));
            } else {
                setState(prev => ({
                    ...prev,
                    error: {
                        state: true,
                        text: "Kullanıcı adı veya şifre hatalı"
                    },
                    redirectToPanel: false
                }));
            }
        } catch (error) {
            setState(prev => ({
                ...prev,
                error: {
                    state: true,
                    text: "Bağlantı hatası"
                }
            }));
        } finally {
            setIsLoading(false);
        }
    };

    if (isAuthenticated || state.redirectToPanel) {
        return <Navigate to='/' />;
    }

    return (
        <div className="min-h-screen w-full flex items-center justify-center bg-background relative overflow-hidden">
            {/* Background Gradient Overlay */}
            <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_center,rgba(253,112,0,0.08)_0%,rgba(0,0,0,0.6)_100%)] pointer-events-none" />

            {/* Login Card */}
            <div className="relative z-10 w-full max-w-[420px] p-8 mx-4">
                <div className="bg-[#121212]/80 backdrop-blur-xl border border-[#ffffff10] rounded-2xl shadow-2xl p-8 animate-fade-in-bottom">

                    {/* Header / Logo */}
                    <div className="flex flex-col items-center mb-8">
                        <div className="mb-4 relative group">
                            <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full group-hover:bg-primary/30 transition-all duration-500" />
                            <img
                                alt="LYSEP Logo"
                                src="/logo/lysep_logo_raw.svg"
                                className="w-24 h-24 relative p-2 animate-spin-menu drop-shadow-[0_0_15px_rgba(253,112,0,0.5)]"
                            />
                        </div>
                        <h1 className="text-2xl font-bold tracking-[0.15em] text-white font-logo">
                            LYSEP <span className="text-primary drop-shadow-[0_0_10px_rgba(253,112,0,0.5)]">PANEL</span>
                        </h1>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                        {/* Username Input */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-400 uppercase tracking-wide ml-1">
                                Kullanıcı Adı
                            </label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <User className="h-5 w-5 text-gray-500 group-focus-within:text-primary transition-colors duration-300" />
                                </div>
                                <input
                                    type="text"
                                    value={state.username}
                                    onChange={(e) => setState({ ...state, username: e.target.value })}
                                    className="block w-full pl-10 pr-3 py-3 bg-[#1a1a1a] border border-[#333] rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all duration-300 sm:text-sm"
                                    placeholder="Kullanıcı adınızı girin"
                                    autoComplete="off"
                                />
                            </div>
                        </div>

                        {/* Password Input */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-400 uppercase tracking-wide ml-1">
                                Şifre
                            </label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-gray-500 group-focus-within:text-primary transition-colors duration-300" />
                                </div>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={state.password}
                                    onChange={(e) => setState({ ...state, password: e.target.value })}
                                    className="block w-full pl-10 pr-10 py-3 bg-[#1a1a1a] border border-[#333] rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all duration-300 sm:text-sm"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-white transition-colors duration-200"
                                >
                                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                        </div>

                        {/* Error Message */}
                        {state.error.state && (
                            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-center animate-fade-in-top">
                                <p className="text-sm text-red-400 font-medium">
                                    {state.error.text}
                                </p>
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`
                                relative w-full flex justify-center items-center py-3.5 px-4 
                                border border-transparent rounded-lg text-sm font-bold text-white uppercase tracking-wider
                                bg-gradient-to-r from-primary to-[#ff8c33] hover:from-[#e66500] hover:to-primary
                                shadow-[0_4px_14px_0_rgba(253,112,0,0.39)] hover:shadow-[0_6px_20px_rgba(253,112,0,0.23)] hover:-translate-y-0.5
                                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary focus:ring-offset-[#121212]
                                disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none
                                transition-all duration-300 ease-out group overflow-hidden mt-2
                            `}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5" />
                                    Giriş Yapılıyor...
                                </>
                            ) : (
                                "Giriş Yap"
                            )}
                        </button>
                    </form>
                </div>

                {/* Footer */}
                <div className="mt-8 text-center animate-fade-in-bottom animation-delay-200">
                    <p className="text-xs text-gray-600">
                        &copy; 2026 LYSEP Inc. Tüm hakları saklıdır.
                    </p>
                </div>
            </div>
        </div>
    );
}
